import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import Nav from 'reactstrap/lib/Nav';
import NavItem from 'reactstrap/lib/NavItem';
import Dropdown from 'reactstrap/lib/Dropdown';
import DropdownItem from 'reactstrap/lib/DropdownItem';
import DropdownToggle from 'reactstrap/lib/DropdownToggle';
import DropdownMenu from 'reactstrap/lib/DropdownMenu';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { connectedProfileAuth } from 'site-modules/shared/components/profile/profile-auth';
import { PriceDropAlerts } from 'site-modules/shared/components/profile/price-drop-alerts/price-drop-alerts';
import { PriceDropAlertsPopover } from 'site-modules/shared/components/profile/price-drop-alerts-popover/price-drop-alerts-popover';
import { handleInsiderMenuOpen } from 'client/engagement-handlers/global-navigation-engagement-handler/global-navigation-engagement-handler';
import { venomHistory } from 'client/utils/history/venom-history';
import { getSavedItemsAmount } from 'site-modules/shared/components/profile/utils/extract-helper';
import { INSIDER_PATHS } from 'site-modules/shared/constants/profile/dashboard-paths';
import { Link } from 'site-modules/shared/components/link/link';
import { GoogleOneTap } from 'site-modules/shared/components/profile/google-one-tap/google-one-tap';
// eslint-disable-next-line venom/no-internal-modules
import { navigationFilter } from 'site-modules/profile/components/shared/insider-navigation/shared/insider-navigation-map';

import { logger } from 'client/utils/logger/logger';
import './insider-section.scss';

const DROPDOWN_ITEM_CLASSES = 'insider-dropdown-item text-gray-darker px-1';
const MY_ACCOUNT_LABEL = 'My Account';

const AUTHORIZATION_LINKS = {
  sign: {
    text: 'Register/Sign In',
    trackingId: 'global_nav_begin_sign_up',
    trackingValue: 'Sign',
  },
  signOut: {
    text: 'Sign Out',
    trackingId: 'global_nav_sign_out',
    trackingValue: 'Sign Out',
  },
  account: {
    href: INSIDER_PATHS.SETTINGS_PAGE,
    text: 'Account',
    trackingId: 'global_nav_click_account',
    trackingValue: 'account',
  },
};

const AUTHORIZED_LINKS = [
  {
    href: INSIDER_PATHS.INVENTORY_PAGE,
    text: 'Saved Inventory',
    trackingId: 'global_nav_click_saved_inventory',
    trackingValue: 'saved-inventory',
    countKey: 'inventory',
    includeAlerts: true,
  },
  {
    href: INSIDER_PATHS.SEARCH_PAGE,
    text: 'Saved Searches',
    trackingId: 'global_nav_click_saved_searches',
    trackingValue: 'saved-searches',
    countKey: 'search',
  },
  {
    href: INSIDER_PATHS.APPRAISAL_PAGE,
    text: 'My Cars for Sale',
    trackingId: 'global_nav_click_trade_in_values',
    trackingValue: 'my-appraisals',
    countKey: 'appraisal',
  },
  {
    href: INSIDER_PATHS.FAVORITE_MODELS,
    text: 'Favorite Models',
    trackingId: 'global_nav_click_fav_models',
    trackingValue: 'saved-models',
    countKey: 'favorite-models',
  },
  {
    href: INSIDER_PATHS.GEAR_GARAGE,
    text: 'Gear Garage',
    trackingId: 'global_nav_click_gear_garage',
    trackingValue: 'gear-garage',
    countKey: 'gear-garage',
  },
];

const renderNavItem = (item, counts, onClick) => {
  const LinkComp = item.href ? Link : 'a';
  const linkProp = item.href ? 'to' : '';
  return (
    <NavItem className="py-0_25 py-md-0" key={item.text}>
      <LinkComp
        {...{ [linkProp]: item.href }}
        onClick={onClick}
        className="nav-link px-0 px-md-1"
        data-tracking-id={item.trackingId}
        data-tracking-value={item.trackingValue}
        rel="nofollow"
        target={item.target}
      >
        <span className="global-nav-link">{item.text}</span>
        {item.countKey && !!counts[item.countKey] && (
          <span className="text-white ps-0_25">({counts[item.countKey]})</span>
        )}
        {item.includeAlerts && <PriceDropAlerts className="pos-r" hideCount />}
      </LinkComp>
    </NavItem>
  );
};

function InsiderSectionUI({
  isAuthenticated = false,
  isAnonymousUser = false,
  isMobile = false,
  className = null,
  isGlobalNavDesktop = false,
  idmData = null,
  toggleSignModal,
  signIn,
  signOut,
  createOrUpdateIdmData,
  privatePartyListing = [],
}) {
  const [dropdownOpen, toggleDropdown] = useToggle(false);
  const counts = { ...getSavedItemsAmount(idmData, privatePartyListing) };

  function doSignOut() {
    signOut();
    venomHistory.push('/');
  }

  async function authenticatedSignUp() {
    try {
      toggleSignModal();
      await createOrUpdateIdmData({
        idmDataToSave: {},
        signInAnonymouslyTracking: { creativeId: 'sign-up-anonymous' },
        skipUpdate: true, // prevent extra calls to IDM
      });
    } catch (error) {
      logger.warn('Profile is not created', error);
    }
    return Promise.resolve();
  }

  useEffect(() => {
    // Fire event for tablet/desktop
    if (dropdownOpen) {
      handleInsiderMenuOpen(false); // isMobile = false
    }
  }, [dropdownOpen]);

  const authorizedLinks = AUTHORIZED_LINKS.filter(item => navigationFilter(item.countKey)).concat(
    isAnonymousUser ? [] : AUTHORIZATION_LINKS.account
  );
  const actionName = isAnonymousUser ? 'sign' : 'signOut';
  const actionMethod = isAnonymousUser ? toggleSignModal : doSignOut;

  const dropdownToggleProps = isAuthenticated
    ? { title: MY_ACCOUNT_LABEL, 'aria-label': MY_ACCOUNT_LABEL }
    : {
        title: `${MY_ACCOUNT_LABEL} Sign-in`,
        'aria-label': `${MY_ACCOUNT_LABEL} Sign-in`,
        'data-tracking-id': 'global_nav_begin_sign_up',
      };

  const myAccountNavItem = (
    <Dropdown
      nav
      isOpen={dropdownOpen}
      toggle={isAuthenticated ? toggleDropdown : authenticatedSignUp}
      className={classnames('insider-dropdown dropdown-m', { 'pos-st': !dropdownOpen })}
    >
      <DropdownToggle
        tag="button"
        nav
        className="btn-link pe-0 ps-0_5 ps-md-1 text-shadow-contrast"
        {...dropdownToggleProps}
      >
        <span className="icon-user3 insider-icon text-white" aria-hidden />
        <span className={classnames('legal text-white ms-0_5', dropdownOpen ? 'icon-arrow-up3' : 'icon-arrow-down3')} />
      </DropdownToggle>
      {isAuthenticated && dropdownOpen && (
        <DropdownMenu end className="insider-dropdown-menu">
          {authorizedLinks.map(link => (
            <DropdownItem
              tag={Link}
              key={link.text}
              role="menuitem"
              to={link.href}
              className={classnames('dropdown-item', DROPDOWN_ITEM_CLASSES)}
              data-tracking-id={link.trackingId}
              data-tracking-value={link.trackingValue}
              rel="nofollow"
              target={link.target}
            >
              {link.text}
              {link.countKey && !!counts[link.countKey] && <span className="ps-0_25">({counts[link.countKey]})</span>}
              {link.includeAlerts && <PriceDropAlerts className="pos-r" hideCount />}
            </DropdownItem>
          ))}
          <DropdownItem
            tag="button"
            role="menuitem"
            onClick={actionMethod}
            className={DROPDOWN_ITEM_CLASSES}
            data-tracking-id={AUTHORIZATION_LINKS[actionName].trackingId}
            data-tracking-value={AUTHORIZATION_LINKS[actionName].trackingValue}
          >
            {AUTHORIZATION_LINKS[actionName].text}
          </DropdownItem>
        </DropdownMenu>
      )}
    </Dropdown>
  );

  const renderUnauthorizedSection = isMobile ? (
    <NavItem>
      <div className="insider-dropdown-menu unauthorized">
        <h3 className="pe-1 text-white">Save cars &amp; get price drop alerts</h3>
        <Button
          className="sign-up-button mt-0_5 w-50 btn-bg-white"
          color="outline-primary-b"
          onClick={authenticatedSignUp}
          data-tracking-id="global_nav_begin_sign_up"
        >
          Sign up
        </Button>
        <div className="mt-1 medium text-white">
          Already have an account?{' '}
          <Button
            color="link"
            className="text-white text-underline text-transform-none p-0 fw-normal"
            onClick={toggleSignModal}
            data-tracking-id="global_nav_begin_sign_in"
          >
            Log in
          </Button>
        </div>
      </div>
    </NavItem>
  ) : (
    myAccountNavItem
  );

  const renderAuthorizedSection = isMobile ? (
    <Fragment>
      {authorizedLinks.map(link => renderNavItem(link, counts))}
      {renderNavItem(AUTHORIZATION_LINKS[actionName], {}, actionMethod)}
    </Fragment>
  ) : (
    myAccountNavItem
  );

  return (
    <Fragment>
      <GoogleOneTap signIn={signIn} />
      <Nav className={classnames('insider-section', className)}>
        {!isMobile && <PriceDropAlertsPopover showAlerts={isAuthenticated} isGlobalNavDesktop={isGlobalNavDesktop} />}
        {isAuthenticated ? renderAuthorizedSection : renderUnauthorizedSection}
      </Nav>
    </Fragment>
  );
}

InsiderSectionUI.propTypes = {
  isMobile: PropTypes.bool,
  toggleSignModal: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  signIn: PropTypes.func.isRequired,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isAnonymousUser: PropTypes.bool,
  idmData: PropTypes.shape({}),
  isGlobalNavDesktop: PropTypes.bool,
  className: PropTypes.string,
  privatePartyListing: PropTypes.arrayOf(PropTypes.shape({})),
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  isAuthenticated: get(state, 'profile.isAuthenticated'),
  isAnonymousUser: get(state, 'profile.isAnonymousUser'),
});

const stateToPropsConfig = {
  idmData: bindToPath(props => (props.isAuthenticated ? 'data.idm' : ''), UserProfileModel),
  privatePartyListing: bindToPath(
    props => (props.isAuthenticated && !props.isAnonymousUser ? 'data.privatePartyListing' : ''),
    UserProfileModel
  ),
};

export const InsiderSection = profileScreenMethods(
  insiderMethods(connectedProfileAuth(connect(mapStateToProps)(connectToModel(InsiderSectionUI, stateToPropsConfig))))
);
